export type QualityTier = "A" | "B" | "C";

export type QualityPreset = {
  tier: QualityTier;
  dprCap: number;
  particleScale: number;
  enableDofHint: boolean;
  enableVapor: boolean;
  reducedMotion: boolean;
};

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isCoarsePointer(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}

function estimateGpuTier(): QualityTier {
  if (prefersReducedMotion()) return "C";

  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl2", { powerPreference: "high-performance" }) ||
    canvas.getContext("webgl");

  if (!gl) return "C";

  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  const renderer = debugInfo
    ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)).toLowerCase()
    : "";

  const cores = navigator.hardwareConcurrency || 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const mobile = isCoarsePointer() || /mobi|android/i.test(navigator.userAgent);

  if (
    renderer.includes("swiftshader") ||
    renderer.includes("llvmpipe") ||
    memory <= 2 ||
    cores <= 2
  ) {
    return "C";
  }

  if (mobile || memory <= 4 || cores <= 4) return "B";
  return "A";
}

export function resolveQuality(): QualityPreset {
  const reducedMotion = prefersReducedMotion();
  const tier = estimateGpuTier();

  if (reducedMotion || tier === "C") {
    return {
      tier: "C",
      dprCap: 1,
      particleScale: 0,
      enableDofHint: false,
      enableVapor: false,
      reducedMotion: true,
    };
  }

  if (tier === "B") {
    return {
      tier: "B",
      dprCap: 1.5,
      particleScale: 0.45,
      enableDofHint: false,
      enableVapor: true,
      reducedMotion: false,
    };
  }

  return {
    tier: "A",
    dprCap: 2,
    particleScale: 1,
    enableDofHint: true,
    enableVapor: true,
    reducedMotion: false,
  };
}
